<script setup>
import { inject, ref, computed, watch, onMounted, nextTick } from 'vue'
import usePhotoswipe from './usePhotoswipe'
import '@/assets/style/editorView.css'
import PhotoGallery from '../PhotoGallery/index.vue'
import { sha1 } from 'js-sha1'
import { getTvbPlayerType, appendUtmParams } from '@/utils'

const props = defineProps({
  content: { type: Array, default: () => [] },
  id: { type: [String, Number], default: null },
  img: { type: String, default: '' },
  thumbnail: { type: String, default: '' }
})

const emit = defineEmits(['viewerShown', 'viewerChange'])

const size = inject('size')
const isMobile = computed(() => ['s', 'xs'].includes(size.value))
const contents = computed(() => handle(props.content))
const images = ref([])
const richTextImgs = computed(() =>
  props.img ? [{ src: props.img, thumbnail: props.thumbnail }, ...images.value] : images.value
)
const changeFn = (pswp) => {
  emit('viewerChange', pswp)
  window.history.replaceState(
    history.state,
    null,
    appendUtmParams(
      location.origin + location.pathname + `?photo_id=${sha1(pswp.currSlide.data.src)}`
    )
  )
}
const { lightbox } = usePhotoswipe(richTextImgs, changeFn)

// 1：richtext 2：图集
const handle = (value = []) => {
  const arr = JSON.parse(JSON.stringify(value))
  for (const item of arr) {
    if (item.type === 1) {
      item.rich_text.html = handleRichtext(item.rich_text.html)
    }
    if (item.type === 2) handleAlbum(item.image)
  }
  return arr
}

const replaceBookmarks = (hash) => {
  return `javascript:window.scrollBy({top: document.getElementById('${hash.substr(
    1
  )}').getBoundingClientRect().top - (document.querySelector('.editor-content-view-mobile') ? 84 : 64)})`
}

const letslazyload = (str) => {
  let regexp1 = new RegExp(/(<img )([^>]*)(src=")([^"]*")([^>]*)(>)/, 'g')
  let res = str.replace(regexp1, function (match, p1, p2, p3, p4, p5, p6) {
    return p1 + p2 + 'data-src="' + p4 + p5 + p6
  })
  return res
}

const handleRichtext = (value) => {
  try {
    const res = new DOMParser().parseFromString(value, 'text/html')
    const imgNodes = res.querySelectorAll('img')
    let imgs = []
    for (const target of imgNodes) {
      // target.loading = 'lazy'
      const desc = target.dataset?.desc
      const href = target.dataset?.href
      const rel = target.dataset?.rel ? (target.dataset.rel ?? 'follow') : 'follow'
      const openType = target.dataset?.target ? (target.dataset.target ?? '_self') : '_self'

      // 处理错误图片
      // const onerror = `javascript:this.src='/image/img_header_logo@2x.png';this.classList.add('tvb-img_error')`
      // let imgHtml = target.outerHTML.replace(/<img/g, `<img onerror="${onerror}"`)
      let imgHtml = letslazyload(target.outerHTML)
      // 处理图片链接和描述
      if (href) {
        imgHtml = `<a class="tvb-img_href" href="${href}" target="${openType}" rel="${rel}" >${imgHtml}</a>`
      } else {
        imgs.push({ src: target.src, 'data-desc': desc || '' })
      }
      imgHtml = `<figure class="tvb-img_des-wrap">${imgHtml}${
        desc ? `<figcaption class='tvb-img_des'>${desc}</figcaption>` : ''
      }</figure>`
      target.outerHTML = imgHtml
    }
    images.value.push(...imgs)

    // 替换书签
    const aNodes = res.querySelectorAll('a')
    for (const target of aNodes) {
      const hash = target?.attributes?.href?.value
      if (hash && hash.trim().indexOf('#') === 0) {
        target.href = replaceBookmarks(hash)
        target.target = ''
      }
    }

    // 检查嵌入式帖子
    const insNode = res.querySelector('.instagram-media')
    const twisNode = res.querySelector('.twitter-tweet')
    if (insNode) hasInsPost.value = true
    if (twisNode) hasXPost.value = true

    return res.body.innerHTML
  } catch (error) {
    return value
  }
}

const hasInsPost = ref(false)
const hasXPost = ref(false)

watch(
  () => [hasInsPost.value, hasXPost.value],
  () => {
    if (hasInsPost.value) {
      setTimeout(() => {
        window.instgrm?.Embeds.process()
      }, 500)
    }
    if (hasXPost.value) {
      setTimeout(() => {
        window.twttr?.widgets.load(document.getElementById(`editor${props.id}`))
      }, 500)
    }
  },
  { flush: 'post' }
)

// 图集
const handleAlbum = (value) => {
  if (value?.images) {
    const res = value.images.map((v) => {
      return { src: v.thumb.large, thumbnail: v.thumb.small, 'data-desc': v.description || '' }
    })
    images.value.push(...res)
  }
}

/**
 * 图片查看器
 */
const findImgIndex = (src) => {
  return richTextImgs.value.findIndex((v) => v.src === src)
}

const showViewer = (src, num = 0) => {
  const findIndex = src ? findImgIndex(src) : num
  const index = findIndex > -1 ? findIndex : 0
  lightbox.value?.loadAndOpen(index)
}

const onRichtextClick = (e) => {
  const target = e.target
  if (target?.localName === 'img') {
    try {
      if (target?.dataset?.href) return
      showViewer(target?.src)
    } catch (error) {
      console.warn(error)
    }
  }
}

const onImgClick = (e) => {
  if (e.jump_url) return
  showViewer(e.thumb.large)
}
watch(contents, () => {
  showVideo()
})
const tvbPlayerRef = ref()
const videoPlayers = ref([])
const videoIndex = ref(1000)
const showVideo = () => {
  videoPlayers.value = []
  document.querySelectorAll('div[data-video-src]').forEach((e) => {
    videoIndex.value = videoIndex.value + 1
    const elId = e.id ? e.id : 'tvb-video-' + videoIndex.value
    e.id = elId
    var videoPlayer = {
      article_id: props.id,
      type: getTvbPlayerType(e.getAttribute('data-video-type')),
      video_id: e.getAttribute('data-video-id'),
      video_url: e.getAttribute('data-video-src'),
      content_id: e.getAttribute('data-content-id'),
      title: e.getAttribute('data-title'),
      program_title: e.getAttribute('data-program-title'),
      cover: e.getAttribute('data-cover-large'),
      video_clarity: {},
      subtitle: {},
      element_id: elId,
      id: ''
    }
    if (e.getAttribute('data-video-clarity')) {
      videoPlayer.video_clarity = JSON.parse(e.getAttribute('data-video-clarity'))
    }
    if (e.getAttribute('data-subtitle')) {
      videoPlayer.subtitle = JSON.parse(e.getAttribute('data-subtitle'))
    }
    videoPlayer.id = `tvb-player-article-video-${videoPlayer.video_id}`
    if (document.getElementById(videoPlayer.id) == null) {
      videoPlayers.value.push(videoPlayer)
    }
  })
}

onMounted(() => {
  lightbox.value.on('initialLayout', () => {
    emit('viewerShown', lightbox.value.pswp)
  })
  lightbox.value.on('close', () => {
    window.history.replaceState(
      history.state,
      null,
      appendUtmParams(location.origin + location.pathname)
    )
  })
  nextTick(() => {
    showVideo()
  })
})
defineExpose({ showViewer, richTextImgs })
</script>

<template>
  <div
    :id="`editor${id}`"
    class="editor-content-view article-editor-content-view"
    :class="{ 'article-editor-content-view-mobile': isMobile }"
  >
    <template v-for="(item, i) in contents" :key="i">
      <div
        v-if="item.type === 1"
        class="richtext"
        :class="{ 'frist-child': i === 0, 'last-child': i === contents.length - 1 }"
        v-html="item.rich_text?.html"
        v-lazy-container="{ selector: 'img' }"
        @click="onRichtextClick"
      ></div>
      <template v-if="item.type === 2 && item.image.images.length > 0">
        <photo-gallery
          class="e-photo-gallery"
          :class="{
            'frist-child': i === 0,
            'last-child': i === contents.length - 1,
            mobile: isMobile
          }"
          :images="item.image.images"
          :mode="item.image.layout === 2 ? 'vertical' : 'horizontal'"
          @imgClick="onImgClick"
        ></photo-gallery>
      </template>
    </template>
    <div v-for="(videoPlayer, index) in videoPlayers" :key="index">
      <Teleport :to="`#${videoPlayer.element_id}`" :key="videoPlayer.element_id">
        <div class="article-editor-content-video">
          <tvb-player
            class="article-editor-content-video-player"
            ref="tvbPlayerRef"
            :id="videoPlayer.id"
            :src="videoPlayer.video_url"
            :type="videoPlayer.type"
            :content-id="videoPlayer.content_id"
            :region-only="videoPlayer?.region_only"
            :title="videoPlayer?.title"
            :video-clarity="videoPlayer?.video_clarity"
            :subtitle="videoPlayer?.subtitle"
            :youbora-options="{
              videoTitle: videoPlayer?.title,
              programTitle: videoPlayer?.program_title,
              videoSrc: videoPlayer?.video_url,
              contentId: videoPlayer?.video_id,
              articleId: videoPlayer?.article_id
            }"
            :mute="true"
            :posterUrl="videoPlayer.cover"
          ></tvb-player>
        </div>
      </Teleport>
    </div>
  </div>
</template>

<style lang="scss">
@use '@/assets/style/photoswipe.scss';

.article-editor-content-view {
  --tvb-editor-view-img-padding: 32px;
  --tvb-editor-view-title-color: #1e1e1e;
  text-align: justify;
  letter-spacing: 0.8px;
  font-size: 18px;
  line-height: 32px;
  overflow: hidden;

  .richtext {
    p,
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      margin-top: var(--tvb-editor-view-img-padding) !important;
      margin-bottom: var(--tvb-editor-view-img-padding) !important;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      color: var(--tvb-editor-view-title-color);
    }

    // &.frist-child {
    //   p:first-child,
    //   figure:first-child.tvb-img_des-wrap,
    //   div:first-child.w-e-post {
    //     margin-top: 0 !important;
    //   }
    // }
    &.last-child {
      p:last-child,
      h1:last-child,
      h2:last-child,
      h3:last-child,
      h4:last-child,
      h5:last-child,
      h6:last-child,
      figure:last-child.tvb-img_des-wrap,
      div:last-child.w-e-post,
      div[data-w-e-type='video']:last-child {
        margin-bottom: 0 !important;
      }
    }

    &.frist-child {
      p:first-child,
      h1:first-child,
      h2:first-child,
      h3:first-child,
      h4:first-child,
      h5:first-child,
      h6:first-child,
      figure:first-child.tvb-img_des-wrap,
      div:first-child.w-e-post,
      div[data-w-e-type='video']:first-child {
        margin-top: 0 !important;
      }
    }

    img {
      max-width: 100%;
      cursor: pointer;
    }

    img[lazy='error'],
    img[lazy='loading'] {
      background-color: #f8f9fa;
      aspect-ratio: 16 / 9;
      padding: 0 calc(50% - (98px / 2));
      width: 100%;
      object-fit: contain;
    }

    iframe {
      display: block;
    }

    .tvb-img_des-wrap {
      margin-top: var(--tvb-editor-view-img-padding);
      margin-bottom: var(--tvb-editor-view-img-padding);
    }

    /* 图片处理 */
    .tvb-img_href {
      text-decoration: none;
    }

    .tvb-img_des {
      margin-top: 12px;
      font-size: 16px;
      color: #818d95;
      line-height: 24px;
      letter-spacing: 0.5px;
      display: block;
      word-break: break-all;
    }

    .tvb-img_error {
      background-color: #f8f9fa;
      aspect-ratio: 16 / 9;
      padding: 0 calc(50% - (98px / 2));
      width: 100%;
      object-fit: contain;
    }
    .article-editor-content-video {
      margin-top: 10px;
      .article-editor-content-video-player {
        width: 100%;
        aspect-ratio: 16/9;
      }
    }
  }

  // 图集
  .e-photo-gallery {
    margin-top: var(--tvb-editor-view-img-padding);
    margin-bottom: var(--tvb-editor-view-img-padding);
    // &.frist-child {
    //   margin-top: 0 !important;
    // }
    &.last-child {
      margin-bottom: 0 !important;
    }

    .tvb-img_href {
      text-decoration: none;
    }

    .tvb-img_des {
      margin-top: 10px;
      font-size: 16px;
      color: #818d95;
      line-height: 22px;
      letter-spacing: 0.5px;
      display: block;
      word-break: break-all;
    }
  }

  // 贴文
  .w-e-post,
  div[data-w-e-type='video'] {
    margin-top: var(--tvb-editor-view-img-padding);
    margin-bottom: var(--tvb-editor-view-img-padding);

    .twitter-tweet,
    .instagram-media {
      margin-top: 0 !important;
      margin-bottom: 0 !important;
    }
  }
}

/* 移动端样式 */
.article-editor-content-view-mobile {
  --tvb-editor-view-img-padding: 20px;
  --tvb-editor-view-title-color: #333;
  letter-spacing: 0.6px;
  line-height: 30px;
  color: #333;

  .richtext {
    img {
      pointer-events: auto;
    }

    .tvb-img_des {
      margin-top: 8px;
      font-size: 15px;
      letter-spacing: 0px;
    }
  }

  .e-photo-gallery {
    .tvb-img_des {
      margin-top: 8px;
      font-size: 15px;
      letter-spacing: 0px;
    }
  }
}
</style>
